import React, { useState, useEffect } from "react";
import ModalBase from "./ModalBase";
import { useFeedback } from "@contexts/FeedbackContext";
import { HiClipboardList, HiPaperAirplane } from "react-icons/hi";

interface FornecedorFormData {
  id: string;
  fornecedorId?: string;
  nome: string;
  email: string;
  telefone: string;
  emailError?: string;
}

interface ModalEnvioItemProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: (
    fornecedores: Array<{
      id?: string;
      fornecedorId?: string;
      email: string;
      telefone: string;
    }>,
  ) => Promise<void>;
  itemNome: string;
  fornecedores: Array<{
    id?: string;
    fornecedorId?: string;
    nome: string;
    email?: string;
    telefone?: string;
  }>;
}

const ModalEnvioItem: React.FC<ModalEnvioItemProps> = ({
  isOpen,
  onClose,
  onSend,
  itemNome,
  fornecedores: fornecedoresIniciais,
}) => {
  const [fornecedores, setFornecedores] = useState<FornecedorFormData[]>([]);
  const [sending, setSending] = useState(false);
  const { showError } = useFeedback();

  // Atualizar sempre que a modal abrir OU quando fornecedoresIniciais mudarem
  useEffect(() => {
    if (isOpen && fornecedoresIniciais && fornecedoresIniciais.length > 0) {
      setFornecedores(
        fornecedoresIniciais.map((f) => ({
          id: f.id || f.fornecedorId || "",
          fornecedorId: f.fornecedorId || f.id,
          nome: f.nome,
          email: f.email || "",
          telefone: f.telefone || "",
          emailError: "",
        })),
      );
    }
  }, [isOpen, fornecedoresIniciais]);

  // Limpar estado ao fechar
  useEffect(() => {
    if (!isOpen) {
      setFornecedores([]);
      setSending(false);
    }
  }, [isOpen]);

  const validateEmail = (email: string): boolean => {
    // Remove espaços em branco e valida formato de e-mail
    const trimmedEmail = email.trim();
    if (!trimmedEmail) return false;

    // Rejeitar e-mails com vírgulas ou outros caracteres inválidos
    if (/[,;\s<>\[\]\(\)\\"']/.test(trimmedEmail)) {
      return false;
    }

    // Regex robusta para validação de e-mail
    // Formato: usuario@dominio.extensao
    const emailRegex =
      /^[a-zA-Z0-9]+([._-][a-zA-Z0-9]+)*@[a-zA-Z0-9]+([.-][a-zA-Z0-9]+)*\.[a-zA-Z]{2,}$/;
    return emailRegex.test(trimmedEmail);
  };

  const handleEmailChange = (index: number, value: string) => {
    const updated = [...fornecedores];
    updated[index].email = value;
    updated[index].emailError = "";
    setFornecedores(updated);
  };

  // const handleTelefoneChange = (index: number, value: string) => {
  //   const updated = [...fornecedores];
  //   updated[index].telefone = value;
  //   setFornecedores(updated);
  // };

  const handleSend = async () => {
    // Validação - trim para remover espaços
    let hasErrors = false;
    const updated = fornecedores.map((f) => {
      let emailError = "";
      const trimmedEmail = f.email.trim();

      if (!trimmedEmail) {
        emailError = "E-mail obrigatório";
        hasErrors = true;
      } else if (!validateEmail(trimmedEmail)) {
        emailError = "E-mail inválido (exemplo: usuario@dominio.com)";
        hasErrors = true;
      }

      return { ...f, email: trimmedEmail, emailError };
    });

    if (hasErrors) {
      setFornecedores(updated);
      return;
    }

    setSending(true);

    try {
      await onSend(
        fornecedores.map((f) => ({
          id: f.id,
          fornecedorId: f.fornecedorId || f.id,
          email: f.email.trim(), // Garantir trim no envio
          telefone: f.telefone,
        })),
      );
      onClose();
    } catch (error) {
      console.error("Erro ao enviar solicitações:", error);
      showError("Erro ao enviar solicitações de orçamento. Tente novamente.");
    } finally {
      setSending(false);
    }
  };

  return (
    <ModalBase isOpen={isOpen} onClose={onClose}>
      <div className="pb-6 border-b border-base-300 mb-6">
        <h2 className="text-xl font-bold mb-2 text-base-content flex items-center gap-2">
          <HiClipboardList className="h-5 w-5" />
          Solicitação de orçamento por item
        </h2>
        <p className="text-sm text-base-content/70">
          <strong>Item da cotação:</strong> {itemNome}
        </p>
        <p className="text-sm text-base-content/70">
          <strong>
            Será enviado para {fornecedores.length} fornecedor(es)
          </strong>
        </p>
      </div>

      <div>
        <p className="text-sm text-base-content/70 mb-4">
          Informe o e-mail e telefone (opcional) para cada fornecedor que deverá
          receber a solicitação:
        </p>

        <div className="space-y-4">
          {fornecedores.map((fornecedor, index) => (
            <div
              key={fornecedor.id}
              className="p-4 border border-base-300 rounded-lg bg-base-50 hover:border-primary transition-colors"
            >
              <div className="mb-4">
                <strong className="text-base-content">{fornecedor.nome}</strong>
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text font-medium">E-mail *</span>
                </label>
                <input
                  id={`email-${index}`}
                  type="email"
                  className={`input input-bordered ${
                    fornecedor.emailError ? "input-error" : ""
                  }`}
                  value={fornecedor.email}
                  onChange={(e) => handleEmailChange(index, e.target.value)}
                  placeholder={
                    fornecedor.email
                      ? "E-mail pré-preenchido do cadastro"
                      : "exemplo@email.com"
                  }
                  disabled={sending}
                />
                {fornecedor.emailError && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {fornecedor.emailError}
                    </span>
                  </label>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end gap-2 border-t border-base-300 pt-4 mt-6">
        <button className="btn btn-ghost" onClick={onClose} disabled={sending}>
          Cancelar
        </button>
        <button
          className="btn btn-primary"
          onClick={handleSend}
          disabled={
            sending ||
            fornecedores.length === 0 ||
            fornecedores.some((f) => !f.email.trim() || !!f.emailError)
          }
        >
          {sending ? (
            <>
              <span className="loading loading-spinner"></span>
              Enviando...
            </>
          ) : (
            <>
              <HiPaperAirplane className="h-4 w-4" />
              Enviar para {fornecedores.length} fornecedor(es)
            </>
          )}
        </button>
      </div>
    </ModalBase>
  );
};

export default ModalEnvioItem;
